import { CreateCreatorProfileResponse, GetCreatorProfileResponse, GetTopicResponse, UpdateCreatorProfileRequest } from '../../types';
import { del, emptyPost, get, logWithAmplitude, post, put, throwHttpError } from './api';

import accountInfo from '../state/account-info';
import organizationInfo from '../state/organization-info';

export async function createCreatorProfile(
    amplitude_callsite: string,
    creator_profile: UpdateCreatorProfileRequest,
    additional_logged_parameters: Object = {}
): Promise<CreateCreatorProfileResponse> {
    const creator_profile_response = await addCreatorProfileInner(creator_profile);
    logWithAmplitude(
        amplitude_callsite,
        'CreatorProfile Created',
        Object.assign(
            {},
            { creator_profile_id: creator_profile_response._id, creator_profile_name: creator_profile.name },
            additional_logged_parameters
        )
    );
    return creator_profile_response;
}

async function addCreatorProfileInner(creator_profile: UpdateCreatorProfileRequest) {
    const response = await fetch(`/api/profiles`, post(creator_profile));
    if (!response.ok) {
        await throwHttpError('An error occurred while creating the creator profile.', response);
    }

    return await (response.json() as Promise<CreateCreatorProfileResponse>);
}

export async function getCreatorProfiles(amplitude_callsite: string) {

    if (!accountInfo.isAuthenticated) {
        return [];
    }
    const organizaiton_id = organizationInfo.id?.$oid;
    const qs = organizaiton_id ? `?organization_id=${organizaiton_id}` : '';
    const response = await fetch(`/api/profiles${qs}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the creator profiles for account.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get All Creator Profiles for account');
    return await (response.json() as Promise<GetCreatorProfileResponse[]>);
}

export async function getCreatorProfile(
    amplitude_callsite: string,
    creator_profile_id: string,
    use_cache?: boolean
) {
    const response = await fetch(`/api/profiles/${creator_profile_id}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the creator profile.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Creator Profile', {
        creator_profile_id,
        use_cache,
    });

    const normalizedResponse = await response.json() as GetCreatorProfileResponse;

    return normalizedResponse;
}

export async function removeCreatorProfile(amplitude_callsite: string, creator_profile_id: string) {
    const response = await fetch(`/api/profiles/${creator_profile_id}`, del());

    if (!response.ok) {
        await throwHttpError('An error occurred while deleting the creator profile.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Delete Creator Profile', {
        creator_profile_id,
    });
}

export async function updateCreatorProfile(
    amplitude_callsite: string,
    creator_profile_id: string,
    creator_profile: UpdateCreatorProfileRequest,
    additional_logged_parameters: Object = {}
) {
    const response = await fetch(`/api/profiles/${creator_profile_id}`, put(creator_profile));
    if (!response.ok) {
        await throwHttpError('An error occurred while updating the creator profile.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'CreatorProfile Updated',
        Object.assign({}, { creator_profile_id }, additional_logged_parameters)
    );
}

export async function addTopicsToCreatorProfile(
    amplitude_callsite: string,
    creator_profile_id: string,
    topic_ids: string[],
    additional_logged_parameters: Object = {}
) {
    const response = await fetch(`/api/profiles/${creator_profile_id}/topics`, (post(topic_ids)));
    if (!response.ok) {
        await throwHttpError('An error occurred while adding the topic the creator profile.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Topic Added To Creator Profile',
        Object.assign({}, { creator_profile_id, topic_ids }, additional_logged_parameters)
    );
}

export async function removeTopicFromCreatorProfile(
    amplitude_callsite: string,
    creator_profile_id: string,
    topic_id: string,
    additional_logged_parameters: Object = {}
) {
    const response = await fetch(`/api/profiles/${creator_profile_id}/topics/${topic_id}`, del());
    if (!response.ok) {
        await throwHttpError('An error occurred while removing the topic from creator profile.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Topic Removed From Creator Profile',
        Object.assign({}, { creator_profile_id, topic_id }, additional_logged_parameters)
    );
}

export async function getTopicsFromCreatorProfile(
    amplitude_callsite: string,
    creator_profile_id: string,
    additional_logged_parameters: Object = {}
) {
    const response = await fetch(`/api/profiles/${creator_profile_id}/topics`, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while getting topics for creator profile.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Get Topics For Creator Profile',
        Object.assign({}, { creator_profile_id }, additional_logged_parameters)
    );

    return await (response.json() as Promise<GetTopicResponse[]>);
}

export async function editPinForCreatorProfileTopic(
    amplitude_callsite: string,
    creator_profile_id: string,
    topic_id: string,
    value: boolean,
    additional_logged_parameters: Object = {}
) {
    const response = await fetch(`/api/profiles/${creator_profile_id}/topics/${topic_id}?value=${value as unknown as string}`, (emptyPost()));
    if (!response.ok) {
        await throwHttpError('An error occurred while adding the topic the creator profile.', response);
    }

    const value_for_log = value ? 'Pin' : 'Remove Pin';
    logWithAmplitude(
        amplitude_callsite,
        `${value_for_log} Topic on Creator Profile`,
        Object.assign({}, { creator_profile_id, topic_id }, additional_logged_parameters)
    );
}
